import React from 'react';
import { inputClass } from './BackgroundFields';
import ColorFillField from './ColorFillField';
import { MENU_TAB_HOVER_OPTIONS } from '../utils/mediaHoverEffects';
import {
  MENU_TAB_VARIANT_OPTIONS,
  resolveMenuTabVariant,
} from '../utils/menuTabAppearance';
import { MENU_LAYOUT_COLUMNS } from '../utils/menuLayout';
import MenuTabShapeFields from './MenuTabShapeFields';

/**
 * Style / survol / position d’un onglet ou bouton menu (clé `kind:id`).
 */
export default function MenuTabEntryAppearanceFields({
  tabKey,
  menuProps,
  tabHoverEffects = {},
  tabAppearances = {},
  onChangeTabHoverEffect,
  onChangeTabAppearance,
  onChangeTabPlacement,
  /** Boutons menu : défaut visuel « bouton plein » */
  defaultVariant = 'inherit',
  freeLayout = false,
}) {
  const hasExplicitHover = Object.prototype.hasOwnProperty.call(tabHoverEffects, tabKey);
  const hoverCfg = tabHoverEffects[tabKey] || {};
  const hoverEffect = hasExplicitHover ? hoverCfg.effect || 'none' : 'auto';
  const hoverIntensity = hoverCfg.intensity ?? 65;

  const hasExplicitAppearance = Object.prototype.hasOwnProperty.call(tabAppearances, tabKey);
  const appearanceCfg = tabAppearances[tabKey] || {};
  const appearanceVariant =
    appearanceCfg.variant ??
    (defaultVariant === 'solid' && !hasExplicitAppearance ? 'solid' : 'inherit');
  const barAlign = appearanceCfg.barAlign || 'inherit';
  const effectiveVariant = resolveMenuTabVariant(menuProps, tabKey);

  const patchAppearance = (patch) => {
    onChangeTabAppearance?.(tabKey, { ...appearanceCfg, ...patch });
  };

  const clearAppearanceColor = (field) => {
    const next = { ...appearanceCfg };
    delete next[field];
    if (Object.keys(next).length === 0) {
      onChangeTabAppearance?.(tabKey, null);
      return;
    }
    onChangeTabAppearance?.(tabKey, next);
  };

  const applyBarAlign = (v) => {
    if (v === 'inherit') {
      const next = { ...appearanceCfg };
      delete next.barAlign;
      if (Object.keys(next).length === 0) {
        onChangeTabAppearance?.(tabKey, null);
      } else {
        onChangeTabAppearance?.(tabKey, next);
      }
      return;
    }
    patchAppearance({ barAlign: v });
    if (v === 'right' && onChangeTabPlacement) {
      onChangeTabPlacement(tabKey, {
        colStart: Math.max(1, MENU_LAYOUT_COLUMNS - 9),
        colSpan: 9,
        rowStart: 1,
        rowSpan: 1,
        align: 'end',
      });
    }
  };

  return (
    <div className="rounded-lg border border-border/80 p-3 space-y-3 bg-background/80">
      <p className="text-xs font-semibold text-foreground">Apparence &amp; position</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
          Style
          <select
            className={inputClass}
            value={appearanceVariant}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'inherit') {
                const next = { ...appearanceCfg };
                delete next.variant;
                if (Object.keys(next).length === 0) {
                  onChangeTabAppearance?.(tabKey, null);
                } else {
                  onChangeTabAppearance?.(tabKey, next);
                }
                return;
              }
              patchAppearance({ variant: v });
            }}
          >
            {MENU_TAB_VARIANT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
          Effet au survol
          <select
            className={inputClass}
            value={hoverEffect}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'auto') {
                onChangeTabHoverEffect?.(tabKey, null);
                return;
              }
              onChangeTabHoverEffect?.(tabKey, {
                effect: v,
                intensity: hoverIntensity,
              });
            }}
          >
            <option value="auto">Automatique</option>
            {MENU_TAB_HOVER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-muted-foreground sm:col-span-2">
          Position sur la barre (ordinateur)
          <select className={inputClass} value={barAlign} onChange={(e) => applyBarAlign(e.target.value)}>
            <option value="inherit">Avec les autres onglets</option>
            <option value="right">Épinglé à droite</option>
          </select>
          {freeLayout ? (
            <span className="text-[10px] text-muted-foreground mt-0.5">
              Disposition libre : l&apos;épinglage place aussi le bouton à droite ; vous pouvez
              encore le glisser sur la barre.
            </span>
          ) : null}
        </label>
        {hoverEffect !== 'none' && hoverEffect !== 'auto' ? (
          <label className="flex flex-col gap-0.5 text-[11px] text-muted-foreground sm:col-span-2">
            Intensité du survol
            <input
              type="range"
              min={10}
              max={100}
              step={5}
              className="w-full mt-1"
              value={hoverIntensity}
              onChange={(e) =>
                onChangeTabHoverEffect?.(tabKey, {
                  effect: hoverEffect,
                  intensity: Number(e.target.value),
                })
              }
            />
          </label>
        ) : null}
        <label className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
          Taille texte (px)
          <input
            type="number"
            min={10}
            max={24}
            className={inputClass}
            value={appearanceCfg.fontSize ?? ''}
            placeholder="Défaut menu"
            onChange={(e) => {
              const n = e.target.value === '' ? undefined : Number(e.target.value) || 14;
              if (n == null) {
                const next = { ...appearanceCfg };
                delete next.fontSize;
                onChangeTabAppearance?.(tabKey, Object.keys(next).length ? next : null);
                return;
              }
              patchAppearance({ fontSize: n });
            }}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
          Épaisseur texte
          <select
            className={inputClass}
            value={appearanceCfg.fontWeight ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                const next = { ...appearanceCfg };
                delete next.fontWeight;
                onChangeTabAppearance?.(tabKey, Object.keys(next).length ? next : null);
                return;
              }
              patchAppearance({ fontWeight: Number(v) });
            }}
          >
            <option value="">Défaut menu</option>
            <option value={500}>Moyen</option>
            <option value={600}>Semi-gras</option>
            <option value={700}>Gras</option>
          </select>
        </label>
        <div className="sm:col-span-2 space-y-2">
          <MenuTabShapeFields
            showInherit
            shape={appearanceCfg.shape}
            borderRadius={appearanceCfg.borderRadius}
            inheritShape={menuProps?.menuTabShape || 'rounded'}
            inheritRadius={menuProps?.menuTabBorderRadius ?? 8}
            onChangeShape={(v) => {
              if (v === '') {
                const next = { ...appearanceCfg };
                delete next.shape;
                delete next.borderRadius;
                onChangeTabAppearance?.(tabKey, Object.keys(next).length ? next : null);
                return;
              }
              patchAppearance({ shape: v });
            }}
            onChangeRadius={(n) => patchAppearance({ borderRadius: n })}
          />
        </div>
      </div>
      <ColorFillField
        nested
        defaultOpen={Boolean(appearanceCfg.textColor)}
        label="Couleur du texte"
        value={appearanceCfg.textColor || menuProps?.textColor}
        onChange={(fill) => patchAppearance({ textColor: fill })}
        fallback="#111827"
      />
      {appearanceCfg.textColor ? (
        <button
          type="button"
          className="text-[11px] text-primary hover:underline"
          onClick={() => clearAppearanceColor('textColor')}
        >
          Texte : revenir au défaut du menu
        </button>
      ) : null}
      {effectiveVariant === 'solid' ? (
        <>
          <ColorFillField
            nested
            defaultOpen={Boolean(appearanceCfg.backgroundColor)}
            label="Couleur de fond"
            value={appearanceCfg.backgroundColor || menuProps?.menuTabSolidBackground}
            onChange={(fill) => patchAppearance({ backgroundColor: fill })}
            fallback="#2563eb"
          />
          {appearanceCfg.backgroundColor ? (
            <button
              type="button"
              className="text-[11px] text-primary hover:underline"
              onClick={() => clearAppearanceColor('backgroundColor')}
            >
              Fond : revenir au défaut du menu
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
